import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { GraphicFileCard } from "./GraphicFileCard";
import { GraphicSendDialog } from "./GraphicSendDialog";
import { GraphicSendsList } from "./GraphicSendsList";

const API = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (u) => (u ? `${API}${u}` : "");

// Marketing Studio › Fichiers graphistes : upload / création IA → SVG + PDF + PNG HD → envoi fournisseur + suivis.
export function GraphicFilesPanel() {
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ usages: [], send_statuses: [] });
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("upload");
  const [form, setForm] = useState({ name: "", usage: "logo", notes: "", colors: 6, vmode: "color", prompt: "", file: null });
  const [sendOpen, setSendOpen] = useState(false);
  const [refreshSends, setRefreshSends] = useState(0);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/graphic-files"), api.get("/graphic-files/meta")]);
      setFiles(a.data.files || []); setMeta(b.data);
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "upload") {
        if (!form.file) return toast.error("Choisis une image PNG/JPG");
        const fd = new FormData();
        fd.append("file", form.file); fd.append("name", form.name); fd.append("usage", form.usage); fd.append("notes", form.notes);
        fd.append("colors", String(form.colors)); fd.append("mode", form.vmode);
        await api.post("/graphic-files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        if (!form.prompt.trim()) return toast.error("Décris le visuel à créer");
        await api.post("/graphic-files/generate", { prompt: form.prompt, name: form.name, usage: form.usage, notes: form.notes, colors: form.colors, mode: form.vmode });
      }
      toast.success("Fichier vectorisé : SVG + PDF + PNG HD prêts");
      setForm({ ...form, name: "", notes: "", prompt: "", file: null });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const inputCls = "bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm w-full";

  return (
    <div className="space-y-6" data-testid="graphic-files-panel">
      <form onSubmit={submit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 grid md:grid-cols-3 gap-3" data-testid="graphic-files-form">
        <div className="md:col-span-3 flex gap-2 text-xs">
          <button type="button" onClick={() => setMode("upload")} data-testid="gf-mode-upload" className={`px-3 py-1.5 rounded ${mode === "upload" ? "bg-amber-500 text-zinc-950 font-semibold" : "bg-zinc-800"}`}>Téléverser une image</button>
          <button type="button" onClick={() => setMode("ia")} data-testid="gf-mode-ia" className={`px-3 py-1.5 rounded ${mode === "ia" ? "bg-amber-500 text-zinc-950 font-semibold" : "bg-zinc-800"}`}>Créer avec l'IA</button>
          <span className="ml-auto text-zinc-500 self-center">Résultat : SVG vectoriel (ouvrable dans Illustrator) + PDF + PNG 3000 px</span>
        </div>
        {mode === "upload"
          ? <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className={inputCls} data-testid="gf-file" />
          : <textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} placeholder="Ex. : emblème tigre stylisé, rouge et noir, style plat, pour short de Muay Thaï" rows={2} className={`${inputCls} md:col-span-2`} data-testid="gf-prompt" />}
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du fichier (ex. Logo Kickboxing 2026)" className={inputCls} data-testid="gf-name" />
        <select value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })} className={inputCls} data-testid="gf-usage">
          {meta.usages.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <label className="text-xs text-zinc-400 flex items-center gap-2">Couleurs max
          <input type="number" min="2" max="8" value={form.colors} onChange={(e) => setForm({ ...form, colors: Number(e.target.value) })} className="w-16 bg-zinc-950 border border-zinc-700 rounded px-2 py-1" data-testid="gf-colors" />
          <select value={form.vmode} onChange={(e) => setForm({ ...form, vmode: e.target.value })} className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1" data-testid="gf-vmode">
            <option value="color">Couleur</option><option value="binary">Noir & blanc</option>
          </select>
        </label>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes pour le graphiste (broderie, taille du logo, emplacement…)" className={`${inputCls} md:col-span-2`} data-testid="gf-notes" />
        <button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold rounded px-4 py-2" data-testid="gf-submit">
          {busy ? "Vectorisation…" : mode === "upload" ? "Vectoriser l'image" : "Créer et vectoriser"}
        </button>
      </form>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-zinc-200">Fichiers ({files.length}) {sel.length > 0 && <span className="text-xs text-amber-300 ml-2">{sel.length} sélectionné(s)</span>}</h3>
        <button onClick={() => setSendOpen(true)} disabled={!sel.length} className="bg-[#D90429] hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold rounded px-4 py-2" data-testid="gf-send-open">✉️ Envoyer au fournisseur / graphiste</button>
      </div>
      {files.length === 0 && <p className="text-sm text-zinc-500 italic" data-testid="gf-empty">Aucun fichier — téléverse un logo ou crée un visuel avec l'IA.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {files.map((f) => <GraphicFileCard key={f.id} f={f} selected={sel.includes(f.id)} onToggle={() => toggle(f.id)} onChanged={load} usages={meta.usages} />)}
      </div>

      {sendOpen && <GraphicSendDialog fileIds={sel} files={files.filter((f) => sel.includes(f.id))} onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); setSel([]); setRefreshSends((n) => n + 1); }} />}
      <GraphicSendsList refreshKey={refreshSends} statuses={meta.send_statuses} />
    </div>
  );
}
